import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { useAuth } from '@/lib/auth.jsx';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const next = new URLSearchParams(location.search).get('next') || '/app/dashboard';
    const reason = new URLSearchParams(location.search).get('reason');

    // Surface a friendly heads-up when the user was kicked out because someone
    // else (or themselves on a different device) signed into their Solo plan
    // account — the single-session enforcement.
    useEffect(() => {
        if (reason === 'signed-in-elsewhere') {
            toast.message('Signed out on this device', {
                description: 'Your account was just signed in on another device. Sign in again to continue here.',
                duration: 8000,
            });
        } else if (reason === 'session-expired') {
            toast.info('Your session expired. Please sign in again.');
        }
    }, [reason]);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Please enter your email and password.');
            return;
        }
        setLoading(true);
        try {
            await login(email, password);
            toast.success('Welcome back!');
            navigate(next);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
            <div className="hidden lg:flex relative qf-hero-bg qf-noise items-end p-12 text-slate-900">
                <div className="relative z-10">
                    <Logo size="lg" />
                    <h2 className="mt-6 text-4xl font-display tracking-tight max-w-md">Welcome back. Time to land more roofs.</h2>
                    <p className="mt-3 text-slate-600 max-w-md">Pick up where you left off — your unlocked leads, drafts, and pipeline are waiting.</p>
                </div>
            </div>
            <div className="flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <Link to="/" className="lg:hidden inline-block mb-6"><Logo /></Link>
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-display text-2xl">Sign in</CardTitle>
                            <p className="text-sm text-slate-500">Welcome back to QuotesFlow.</p>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="password">Password</Label>
                                        <Link to="/forgot-password" className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2" data-testid="login-forgot-password-link">
                                            Forgot password?
                                        </Link>
                                    </div>
                                    <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
                                </div>
                                <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="login-submit-button">
                                    {loading ? 'Signing in…' : 'Sign in'}
                                </Button>
                                <p className="text-center text-sm text-slate-600">
                                    No account? <Link to="/signup" className="text-slate-900 font-medium underline" data-testid="login-to-signup-link">Create one</Link>
                                </p>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
